/**
 * Preset registry + merge for the canonical faceted-browse pages.
 *
 * Each canonical container (`/documents`, `/delegations`, `/glossary`,
 * `/people`) renders ONE stock search block whose base query is the family's
 * portal_type. A `?preset=<id>` deep link merges the named preset's extra
 * criteria / sort into that block's data at render time (see
 * ./components/withPresetData) — reproducing what used to be a separate
 * hand-built variant page, without a page.
 *
 * Each preset id is the LEGACY variant page's id, so an old `/…/browse-delegations`
 * link maps to `/directives/delegations?preset=browse-delegations`. Criteria are
 * authored in UNIFIED index names + REAL catalog values (verified 2026-07-14
 * against each site's @querystring vocabularies). A preset only ever NARROWS its
 * family, so it never restates the family portal_type — the sole exception is the
 * glossary `definitions` preset, which narrows the Definition+Acronym family down
 * to Definitions. This is block data, not a hardcoded field list: the generation
 * source is the versioned backend inventory
 * (scripts/audit/data/browse_inventory_<site>.json, buckets a/b).
 *
 * Scope: presets exist for the PUBLISHED, user-facing browse variants only.
 * Private editor-only pages (mailing lists, counts, writer rosters) carried a
 * search block but are internal tooling deleted in Task 6 with no public URL to
 * preserve, so they get no preset. Bucket-b pages (empty query) add no filter
 * over the family default page and are likewise omitted. Legacy pages with no
 * distinct query beyond another preset's (5-year-sunset-review ≈ current
 * standards; copy_of_recently-superseded-directives ≈ recently-superseded) are
 * folded into that preset.
 */
import type { SiteId } from './siteConfig/types';
import { STATUS_RETIRED } from './config/searchRegistry.generated';

export interface PresetCriterion {
  i: string;
  o: string;
  v: unknown;
}

export interface Preset {
  id: string;
  label: string;
  criteria: PresetCriterion[];
  sort_on?: string;
  sort_order?: 'ascending' | 'descending';
  b_size?: number;
  /** stock search-block facet defs to add for this preset only */
  facets_add?: Record<string, unknown>[];
  /** facet field values to hide for this preset only */
  facets_hide?: string[];
  /** editorial intro copy, one paragraph per string; rendered above the listing when present */
  intro?: string[];
  /**
   * Live result count below which this preset is withheld from search engines:
   * BrowseSEO emits `noindex,follow` and express/sitemapBrowse leaves the URL
   * out. The page still renders and BrowseNav still links it, so the category
   * keeps its navigational job and its crawl path either way.
   *
   * Defaults to 1, which is the rule every preset already followed: a category
   * matching nothing is not worth submitting. A higher value is for a large
   * generated taxonomy whose long tail is genuinely thin (see
   * SERIES_MIN_TOTAL). Driven by the MEASURED total, so it lifts by itself if
   * the archive grows.
   */
  minTotal?: number;
  /**
   * Optional nav grouping key. Ungrouped presets render in the curated strip;
   * grouped ones render under their own heading, so a 40-entry taxonomy does
   * not turn the strip into a wall of identical chips.
   */
  group?: string;
}

/** site -> family -> presetId -> Preset */
export type PresetRegistry = Record<
  string,
  Record<string, Record<string, Preset>>
>;

const ANY = 'plone.app.querystring.operation.selection.any';
const SINCE = 'plone.app.querystring.operation.date.largerThanRelativeDate';

/**
 * Every retired status value: search-registry.json's Archive group. An
 * archive page filters on all of them, not on the subset its family
 * happens to carry today, so a retired value the registry learns
 * (`Archived`, 2026-10-12) reaches every archive page without a second
 * edit. No page's results changed when this replaced the per-page lists:
 * no family carried a retired value its own list omitted [Solr pivot
 * site,portal_type,status, 2026-10-12]. presets.test.js holds the rule.
 */
const RETIRED: string[] = [...STATUS_RETIRED];

/**
 * The directives series taxonomy -- the DOE subject-classification series a
 * directive belongs to, and the only facet on that archive with a real
 * hierarchy behind it.
 *
 * WHY THESE ARE PRESETS AND NOT JUST A FACET. `?series=<value>` already
 * filters correctly and has since the facet shipped, but a raw facet query is
 * not an indexable page. BrowseSEO builds the canonical from `preset` and the
 * page number ALONE (deliberately -- see its docstring), so `?series=X`
 * canonicalises to the bare `/documents` and `?series=X&page=2` canonicalises
 * to `?page=2`: the first declares itself a duplicate of a page listing
 * everything, the second declares page 2 of one series a duplicate of page 2
 * of another result set entirely. Both were measured on 2026-09-20. Routing
 * the same filter through a preset instead gives it the four things this
 * file's machinery already provides, with no new mechanism: validation in
 * `getPreset`, a self-referencing canonical and a real title in BrowseSEO, a
 * crawlable `<a href>` in BrowseNav, and a sitemap entry in
 * express/sitemapBrowse.
 *
 * WHY HERE AND NOT ON THE OTHER TWO ARCHIVES. Crawl depth. Directives holds
 * 1,802 documents behind pagination that is 19 hops deep at its worst;
 * standards (597) and nnsa (387) bottom out at 6. Index coverage measured the
 * same day tracks that exactly -- directives 16.7%, standards 83.3%, nnsa
 * 90.0% over 60-URL samples -- and Search Console's `referringUrls` for a
 * sampled directives document names only `sitemap1.xml.gz`, meaning nothing
 * on the site links to it. Thin content is not the discriminator: nnsa has
 * the shortest documents of the three (median 4,470 words) and the best
 * coverage. These 40 pages are the shallow route in, putting every document
 * within a couple of hops of the container instead of nineteen.
 *
 * Values are the exact `series` catalog index values, read from the live
 * index on 2026-09-20: 41 distinct values over 1,798 objects. The trailing
 * comment on each line is that day's document count, documentation only --
 * the LIVE count is what decides indexing, via `minTotal` below.
 *
 * The 41st value is absent on purpose. "David Huizenga" is a person's name in
 * a series field, one object, a data defect rather than a category; it needs
 * fixing on the object, not publishing as a landing page. The document itself
 * stays in the main sitemap either way.
 *
 * NUMERIC order (0000, 100, 200 ... 500, 1100 ... 6400), not string order.
 * Registry order is display order, in the Series facet and the series list,
 * and string order put 1100 through 1800 between 100 and 200. Baymard's
 * testing found alphabetical order a dead end for users, and GOV.UK's design
 * system asks for the logical order where one exists; for a numbered
 * classification this is it. Pinned by seriesPresets.test.js.
 */
const DIRECTIVES_SERIES: string[] = [
  '0000 Subject Classification', // 3
  '100 Leadership/Management Planning', // 182
  '200 Information and Analysis', // 347
  '300 Human Resources', // 125
  '400 Work Processes', // 712
  '500 Business and Support Services', // 76
  '1100 Org. Authorities, Functions and Internal Relationships', // 11
  '1200 External Relationships', // 12
  '1300 Management Systems and Standards', // 39
  '1400 Administrative Support Services', // 8
  '1500 Travel and Transportation', // 6
  '1600 Equal Opportunity', // 6
  '1700 Freedom of Information', // 1
  '1800 Privacy', // 1
  '2000 Legal', // 2
  '2100 Financial Management - General', // 6
  '2300 Auditing', // 5
  '3100 Reserved', // 1
  '3200 Personnel Provisions - General', // 5
  '3300 Employment', // 15
  '3400 Employee Performance and Utilization', // 4
  '3500 Position Classification, Pay and Allowances', // 3
  '3600 Attendance and Leave', // 3
  '3700 Personnel Relations and Services', // 11
  '3800 Insurance and Annuities', // 2
  '4000 Logistics Management', // 1
  '4200 Procurement', // 12
  '4300 Real Property Management', // 4
  '4500 Commercial and Industrial Activities', // 1
  '4700 Project Management', // 5
  '5000 Program Planning & Management', // 6
  '5100 Planning, Programming, & Budgeting', // 9
  '5300 Telecommunications', // 4
  '5400 Environmental Quality and Impact', // 64
  '5500 Emergency Preparedness', // 16
  '5600 Defense Programs', // 71
  '5700 Energy Programs & Policies - General', // 8
  '5800 Energy Research & Technology', // 4
  '5900 Energy Information', // 1
  '6400 Construction and Engineering', // 5
];

/**
 * Below this many live results a series page is linked and browsable but NOT
 * offered to search engines (see `Preset.minTotal`).
 *
 * 10 is not arbitrary. 14 of the 40 series clear it and they cover 1,693 of
 * the 1,798 classified documents, 94 per cent, so the crawl-depth problem is
 * solved by the 14 alone. The remaining 26 average 4 documents each, and
 * submitting a pile of near-empty category pages to the sitemap of the
 * archive that was just deindexed for thin content would repeat the exact
 * mistake this work exists to undo. Every series still gets a link, which is
 * what actually shortens the path to a document; only index candidacy is
 * gated.
 */
export const SERIES_MIN_TOTAL = 10;

/** `500 Business and Support Services` -> `series-500-business-and-support-services` */
export const seriesPresetId = (value: string): string =>
  `series-${value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')}`;

/**
 * One preset per series value. No `sort_on`: a preset only ever NARROWS its
 * family, and the container's own default ordering is already deterministic,
 * which is all pagination and crawling need.
 */
const seriesPresets = (values: string[]): Record<string, Preset> => {
  const out: Record<string, Preset> = {};
  for (const value of values) {
    const id = seriesPresetId(value);
    out[id] = {
      id,
      label: value,
      group: 'series',
      minTotal: SERIES_MIN_TOTAL,
      criteria: [{ i: 'series', o: ANY, v: [value] }],
    };
  }
  return out;
};

const DIRECTIVES_SERIES_PRESETS = seriesPresets(DIRECTIVES_SERIES);

export const PRESETS: PresetRegistry = {
  standards: {
    documents: {
      /**
       * Recently added to THIS archive, newest acquisition first.
       *
       * Ordered by `created` -- when the object entered our ZODB -- which is
       * deliberately NOT the `issue_date` ordering used everywhere else in
       * this file. Both are legitimate and they answer different questions:
       * `issue_date` is when DOE issued the document, `created` is when this
       * archive acquired it. A revision DOE issued long ago can arrive here
       * today, and only `created` can surface that.
       *
       * Why this exists (2026-08-29): the 45 directives acquired 2026-08-15
       * ALL share issue_date 2026-08-05, so SiteHome's 3-row issue_date table
       * showed 3 of them and the other 42 had no inbound internal link
       * anywhere on the site. `gsc inspect` confirmed Google knew those URLs
       * from the sitemap alone ("nothing on the site links to this page") and
       * had still never fetched them 14 days on (empty lastCrawlTime), while
       * Bing had indexed them and sent 288 sessions to just five of them in
       * 28 days. Sitemap presence is demonstrably not enough for Google here;
       * a shallow inbound link is what is missing.
       *
       * No date criterion: ordering alone surfaces new arrivals and, unlike a
       * "last N days" filter, can never render empty. `created` is registered
       * and sortable in plone.app.querystring on all three sites [verified
       * 2026-08-29 via each site's /@querystring]. Empty `criteria` is
       * intentional -- superseded and cancelled revisions are acquisitions
       * too, and excluding them would recreate the orphaning this fixes.
       */
      'recently-added': {
        id: 'recently-added',
        label: 'Recently Added to This Archive',
        criteria: [],
        sort_on: 'created',
        sort_order: 'descending',
        intro: [
          'Documents most recently added to this archive, newest first. This orders by when the archive acquired a document, which is not the same as when DOE issued it -- a revision issued some time ago may have been added here recently.',
        ],
      },
      'standards-browse': {
        id: 'standards-browse',
        label: 'Current Standards',
        criteria: [{ i: 'status', o: ANY, v: ['Current'] }],
        sort_on: 'sortable_title',
        sort_order: 'ascending',
      },
      'archives-browse': {
        id: 'archives-browse',
        label: 'Archived Standards',
        criteria: [{ i: 'status', o: ANY, v: RETIRED }],
        sort_on: 'sortable_title',
        sort_order: 'ascending',
        intro: [
          'Technical standards that have moved out of current status — archived or formally canceled — stay here at full text rather than being removed once a newer revision takes over.',
          "An archived standard's version history shows what it replaced and, where applicable, what current standard replaced it — useful for tracing which requirements applied to a facility or design at a given point in time.",
        ],
      },
    },
  },
  directives: {
    documents: {
      /**
       * Recently added to THIS archive, newest acquisition first.
       *
       * Ordered by `created` -- when the object entered our ZODB -- which is
       * deliberately NOT the `issue_date` ordering used everywhere else in
       * this file. Both are legitimate and they answer different questions:
       * `issue_date` is when DOE issued the document, `created` is when this
       * archive acquired it. A revision DOE issued long ago can arrive here
       * today, and only `created` can surface that.
       *
       * Why this exists (2026-08-29): the 45 directives acquired 2026-08-15
       * ALL share issue_date 2026-08-05, so SiteHome's 3-row issue_date table
       * showed 3 of them and the other 42 had no inbound internal link
       * anywhere on the site. `gsc inspect` confirmed Google knew those URLs
       * from the sitemap alone ("nothing on the site links to this page") and
       * had still never fetched them 14 days on (empty lastCrawlTime), while
       * Bing had indexed them and sent 288 sessions to just five of them in
       * 28 days. Sitemap presence is demonstrably not enough for Google here;
       * a shallow inbound link is what is missing.
       *
       * No date criterion: ordering alone surfaces new arrivals and, unlike a
       * "last N days" filter, can never render empty. `created` is registered
       * and sortable in plone.app.querystring on all three sites [verified
       * 2026-08-29 via each site's /@querystring]. Empty `criteria` is
       * intentional -- superseded and cancelled revisions are acquisitions
       * too, and excluding them would recreate the orphaning this fixes.
       */
      'recently-added': {
        id: 'recently-added',
        label: 'Recently Added to This Archive',
        criteria: [],
        sort_on: 'created',
        sort_order: 'descending',
        intro: [
          'Documents most recently added to this archive, newest first. This orders by when the archive acquired a document, which is not the same as when DOE issued it -- a revision issued some time ago may have been added here recently.',
        ],
      },
      'directives-browse': {
        id: 'directives-browse',
        label: 'Current Directives',
        criteria: [
          { i: 'status', o: ANY, v: ['Current'] },
          {
            i: 'document_category',
            o: ANY,
            v: ['Order', 'Manual', 'Policy', 'Notice', 'Cancellation Notice'],
          },
        ],
        sort_on: 'docid',
        sort_order: 'ascending',
      },
      'browse-archived-directives': {
        id: 'browse-archived-directives',
        label: 'Archived Directives',
        criteria: [
          { i: 'status', o: ANY, v: RETIRED },
          {
            i: 'document_category',
            o: ANY,
            v: ['Cancellation Notice', 'Manual', 'Notice', 'Order', 'Policy'],
          },
        ],
        sort_on: 'sortable_title',
        sort_order: 'ascending',
        intro: [
          "Every DOE directive that has left current status — superseded by a later revision, formally canceled, or otherwise retired — stays here in full, rather than being removed once it's no longer in effect.",
          "DOE's own site drops a directive once something newer replaces it, so this is often the only place the original text of an earlier revision remains online. Each entry links to its full version history, showing exactly what it replaced and what replaced it in turn.",
        ],
      },
      guidance: {
        id: 'guidance',
        label: 'Guidance',
        criteria: [
          { i: 'document_category', o: ANY, v: ['Guide'] },
          { i: 'status', o: ANY, v: ['Current'] },
        ],
        sort_on: 'docid',
        sort_order: 'ascending',
      },
      'guidance-archives': {
        id: 'guidance-archives',
        label: 'Archived Guidance',
        criteria: [
          { i: 'document_category', o: ANY, v: ['Guide'] },
          { i: 'status', o: ANY, v: RETIRED },
        ],
        sort_on: 'docid',
        sort_order: 'ascending',
      },
      'issued-updated': {
        id: 'issued-updated',
        label: 'Recently Issued & Updated',
        criteria: [
          { i: 'updated_date', o: SINCE, v: '90' },
          {
            i: 'document_category',
            o: ANY,
            v: [
              'Cancellation Notice',
              'Guide',
              'Manual',
              'Notice',
              'Order',
              'Policy',
            ],
          },
          { i: 'status', o: ANY, v: ['Current'] },
        ],
        sort_on: 'updated_date',
        sort_order: 'descending',
      },
      canceled: {
        id: 'canceled',
        label: 'Recently Canceled',
        criteria: [
          { i: 'status', o: ANY, v: RETIRED },
          {
            i: 'document_category',
            o: ANY,
            v: [
              'Guide',
              'Manual',
              'Notice',
              'Order',
              'Policy',
              'Cancellation Notice',
            ],
          },
          { i: 'updated_date', o: SINCE, v: '90' },
        ],
        sort_on: 'updated_date',
        sort_order: 'descending',
      },
      ...DIRECTIVES_SERIES_PRESETS,
    },
    delegations: {
      'browse-delegations': {
        id: 'browse-delegations',
        label: 'Current Delegations',
        criteria: [
          { i: 'status', o: ANY, v: ['Current'] },
          { i: 'delegation_type', o: ANY, v: ['Delegation', 'Re-delegation'] },
        ],
        sort_on: 'docid',
        sort_order: 'ascending',
      },
      'browse-designations': {
        id: 'browse-designations',
        label: 'Designations',
        criteria: [
          { i: 'delegation_type', o: ANY, v: ['Designation'] },
          { i: 'status', o: ANY, v: ['Current'] },
        ],
        sort_on: 'docid',
        sort_order: 'ascending',
      },
      'browse-archived-delegations': {
        id: 'browse-archived-delegations',
        label: 'Archived Delegations',
        criteria: [
          { i: 'status', o: ANY, v: RETIRED },
          {
            i: 'delegation_type',
            o: ANY,
            v: ['Delegation', 'Designation', 'Re-delegation'],
          },
        ],
        sort_on: 'docid',
        sort_order: 'ascending',
      },
      'recently-rescinded-delegations': {
        id: 'recently-rescinded-delegations',
        label: 'Recently Rescinded Delegations',
        criteria: [
          {
            i: 'delegation_type',
            o: ANY,
            v: ['Delegation', 'Designation', 'Re-delegation'],
          },
          { i: 'status', o: ANY, v: ['Current'] },
          { i: 'effective', o: SINCE, v: '90' },
        ],
        sort_on: 'effective',
        sort_order: 'descending',
      },
    },
    glossary: {
      definitions: {
        id: 'definitions',
        label: 'Definitions',
        criteria: [{ i: 'portal_type', o: ANY, v: ['Definition'] }],
        sort_on: 'sortable_title',
        sort_order: 'ascending',
      },
    },
  },
  nnsa: {
    documents: {
      /**
       * Recently added to THIS archive, newest acquisition first.
       *
       * Ordered by `created` -- when the object entered our ZODB -- which is
       * deliberately NOT the `issue_date` ordering used everywhere else in
       * this file. Both are legitimate and they answer different questions:
       * `issue_date` is when DOE issued the document, `created` is when this
       * archive acquired it. A revision DOE issued long ago can arrive here
       * today, and only `created` can surface that.
       *
       * Why this exists (2026-08-29): the 45 directives acquired 2026-08-15
       * ALL share issue_date 2026-08-05, so SiteHome's 3-row issue_date table
       * showed 3 of them and the other 42 had no inbound internal link
       * anywhere on the site. `gsc inspect` confirmed Google knew those URLs
       * from the sitemap alone ("nothing on the site links to this page") and
       * had still never fetched them 14 days on (empty lastCrawlTime), while
       * Bing had indexed them and sent 288 sessions to just five of them in
       * 28 days. Sitemap presence is demonstrably not enough for Google here;
       * a shallow inbound link is what is missing.
       *
       * No date criterion: ordering alone surfaces new arrivals and, unlike a
       * "last N days" filter, can never render empty. `created` is registered
       * and sortable in plone.app.querystring on all three sites [verified
       * 2026-08-29 via each site's /@querystring]. Empty `criteria` is
       * intentional -- superseded and cancelled revisions are acquisitions
       * too, and excluding them would recreate the orphaning this fixes.
       */
      'recently-added': {
        id: 'recently-added',
        label: 'Recently Added to This Archive',
        criteria: [],
        sort_on: 'created',
        sort_order: 'descending',
        intro: [
          'Documents most recently added to this archive, newest first. This orders by when the archive acquired a document, which is not the same as when DOE issued it -- a revision issued some time ago may have been added here recently.',
        ],
      },
      'nnsa-directives-browse': {
        id: 'nnsa-directives-browse',
        label: 'Current Directives',
        criteria: [
          { i: 'status', o: ANY, v: ['Current'] },
          {
            i: 'document_category',
            o: ANY,
            v: [
              'Advance Change Directive (ACD)',
              'Business Operating Procedure (BOP)',
              'Supplemental Directive (SD)',
              'NNSA Policy (NAP)',
            ],
          },
        ],
        sort_on: 'docid',
        sort_order: 'ascending',
      },
      'nnsa-archived-directives': {
        id: 'nnsa-archived-directives',
        label: 'Archived Directives',
        criteria: [
          { i: 'status', o: ANY, v: RETIRED },
          {
            i: 'document_category',
            o: ANY,
            v: [
              'Advance Change Directive (ACD)',
              'Business Operating Procedure (BOP)',
              'NNSA Policy (NAP)',
              'Supplemental Directive (SD)',
            ],
          },
        ],
        sort_on: 'docid',
        sort_order: 'ascending',
        intro: [
          'Every NNSA directive — order, policy, or supplemental directive — that has moved out of current status stays here at full text rather than being removed.',
          "NNSA's own site drops a directive once it's superseded; this archive keeps each prior revision, linked to its full version history so you can see exactly what changed and when.",
        ],
      },
      'recently-superseded-directives': {
        id: 'recently-superseded-directives',
        label: 'Recently Superseded',
        criteria: [{ i: 'supersedes_date', o: SINCE, v: '100' }],
        sort_on: 'supersedes_date',
        sort_order: 'descending',
      },
    },
    delegations: {
      designations: {
        id: 'designations',
        label: 'Designations',
        criteria: [
          { i: 'delegation_type', o: ANY, v: ['Designation'] },
          { i: 'status', o: ANY, v: ['Current'] },
        ],
        sort_on: 'docid',
        sort_order: 'ascending',
      },
    },
  },
};

/**
 * Every preset defined for a site+family, in registry order — which is the
 * order the browse nav renders them in, so the registry is the single place
 * that ordering is decided.
 */
export function presetsFor(site?: SiteId | string, family?: string): Preset[] {
  if (!site || !family) return [];
  return Object.values(PRESETS[site]?.[family] ?? {});
}

/**
 * Which facet values have a page of their own: `{ [index]: { [value]: id } }`,
 * in registry order.
 *
 * A GROUPED preset whose only criterion is one value on one index is that
 * value's landing page. The directives Series facet uses this to go to
 * `?preset=series-...` instead of filtering in place, so a series has one URL
 * however a visitor reaches it, and the facet can offer every series from any
 * page (see LeftColumnFacets and Facets). Curated presets carry no `group`
 * and never route.
 */
export function facetPresetRoutes(
  site?: SiteId | string,
  family?: string,
): Record<string, Record<string, string>> {
  const routes: Record<string, Record<string, string>> = {};
  for (const preset of presetsFor(site, family)) {
    if (!preset.group || preset.criteria.length !== 1) continue;
    const { i, v } = preset.criteria[0];
    const values = Array.isArray(v) ? v : [v];
    if (values.length !== 1 || typeof values[0] !== 'string') continue;
    (routes[i] = routes[i] || {})[values[0]] = preset.id;
  }
  return routes;
}

export function getPreset(
  site?: SiteId | string,
  family?: string,
  id?: string | null,
): Preset | undefined {
  if (!site || !family || !id) return undefined;
  return PRESETS[site]?.[family]?.[id];
}

/**
 * Pure, non-destructive merge of a preset into a block's `data`. Appends the
 * preset's criteria to `data.query.query`, overrides sort/b_size, and adds or
 * hides facets. Shallow-clones `data` and `data.query`; never mutates the input.
 * Returns the input unchanged when `preset` is falsy.
 *
 * A preset criterion REPLACES a base row on the same index rather than joining
 * it. Two rows on one index do not narrow: the Solr seam merges every row on
 * an index into one value list (services/querystring_search.py `_filters`),
 * so they union. And core's withSearch seeds a facet from the FIRST base row
 * on its index, then appends that facet's row to the request. The one preset
 * that restates a base index, glossary `definitions`, therefore rendered
 * Definitions on the server and Definitions + Acronyms in every browser: 3,614
 * rows, both types [Chrome, live store, 2026-09-27]. Replacing is exact
 * because a preset only ever narrows its family (see the header).
 */
export function applyPreset(data: any, preset?: Preset): any {
  if (!preset || !data) return data;
  const out = { ...data };
  const narrowed = new Set(preset.criteria.map((c) => c.i));
  const baseQuery = (out.query?.query ?? []).filter(
    (c: any) => !narrowed.has(c?.i),
  );
  out.query = { ...out.query, query: [...baseQuery, ...preset.criteria] };
  if (preset.sort_on) out.query.sort_on = preset.sort_on;
  if (preset.sort_order) out.query.sort_order = preset.sort_order;
  if (preset.b_size) out.query.b_size = preset.b_size;
  if (preset.facets_add || preset.facets_hide) {
    const hidden = new Set(preset.facets_hide ?? []);
    out.facets = [
      ...(out.facets ?? []).filter((f: any) => !hidden.has(f?.field?.value)),
      ...(preset.facets_add ?? []),
    ];
  }
  return out;
}

export default PRESETS;
